import { StaticImport } from 'next/dist/shared/lib/get-img-props';
import Image from 'next/image';
import { ReactNode } from 'react';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useCurrencyCode from '@alltrails/context/hooks/useCurrencyCode';
import usePlans from '@alltrails/context/hooks/usePlans';
import useUser from '@alltrails/context/hooks/useUser';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import { BREAKPOINT_SM_SIZE } from '@alltrails/denali/tokens';
import { ModalAriaLabelledByHeading } from '@alltrails/modal';
import TrialReminder from '@alltrails/payment/components/CheckoutBase/TrialReminder';
import BaseUpsellModal from '../BaseUpsell/BaseUpsellModal';
import styles from '../FeatureUpsellModal.module.scss';
import getCtaText from '../getCtaText';
import PeakFeatureList from '../PeakFeatureList/PeakFeatureList';
import TierSubheading from '../UpsellContent/TierSubheading';

type Props = {
  headerImageSrc: StaticImport;
  heading: ReactNode;
  isOpen: boolean;
  onCloseRequest: () => void;
  promoEligibleHeading: ReactNode;
  upsellTrigger: UpsellTrigger;
};

// https://www.figma.com/design/PtYSSpohQ9rL7ZWCcdsYJK/2025-Upsell-System?node-id=5543-297520&t=9h0uykTwgb7vGafI-0
export default function PlusFeatureUpsellModal({ heading, promoEligibleHeading, onCloseRequest, headerImageSrc, isOpen, upsellTrigger }: Props) {
  const user = useUser();
  const isPromoEligible = !user || user?.isPromoEligible;
  const currencyCode = useCurrencyCode();
  const plans = usePlans();

  return (
    <BaseUpsellModal
      headerImage={<Image alt="" className={styles.image} sizes={`(max-width: ${BREAKPOINT_SM_SIZE}px) 100vw, 480px`} src={headerImageSrc} />}
      heading={heading}
      isOpen={isOpen}
      onCloseRequest={onCloseRequest}
      primaryButtonText={getCtaText('plus', user?.isPromoEligible)}
      promoEligibleHeading={promoEligibleHeading}
      purchaseTier="plus"
      tallerOnMobile
      upsellTrigger={upsellTrigger}
      productsAvailable="plus-peak"
    >
      <PlusLogoBadge className={styles.logo} size="md" variant="full" />
      <ModalAriaLabelledByHeading as="h2" size="400">
        {isPromoEligible ? promoEligibleHeading : heading}
      </ModalAriaLabelledByHeading>
      <div className={styles.subheading}>
        <TierSubheading tier="plus" />
      </div>
      {isPromoEligible && (
        <div className={styles.trialTimeline}>
          <TrialReminder selected tier="plus" currencyCode={currencyCode} cost={plans.plus.priceTotal} variant="default" />
        </div>
      )}
      <PeakFeatureList hasHeaderScrollMargin tierSelected="plus" upsellTrigger={upsellTrigger} hidePeak />
    </BaseUpsellModal>
  );
}
